import { useEffect, useRef, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { cva } from "class-variance-authority";

import { cn } from "../lib/utils";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

/** Jeden wygląd zakładek w całej aplikacji.
 *  - `TabBar`    — zakładki sterowane stanem (shadcn Tabs).
 *  - `NavTabBar` — zakładki sterowane trasą (react-router NavLink), ten sam wygląd.
 *  Rozmiary: `md` (paski główne), `sm` (podzakładki wewnątrz sekcji). */

/** Uwaga: `TabsList`/`TabsTrigger` z `ui/tabs` wnoszą własne klasy bazowe
 *  (`h-9`, `bg-crane-muted`, `rounded-crane-lg`, `justify-center`, `data-[state=active]:bg-crane-background`).
 *  `cn()` = tailwind-merge, więc klasy poniżej wygrywają konflikty — ale `justify-start`
 *  trzeba podać jawnie: odziedziczone `justify-center` przy `overflow-x-auto` ucina
 *  i blokuje dostęp do zakładek wychodzących poza lewą krawędź paska. */
/** Zakładka zachowuje szerokość wynikającą z etykiety. Reguła jednej piątej
 *  dotyczy kolumn treści bloków, nie paska zakładek, więc stała szerokość
 *  ucinałaby dłuższe etykiety. */
export const tabListClass = cva(
  "flex h-auto w-auto justify-start gap-1.5 overflow-x-auto overflow-y-hidden whitespace-nowrap rounded-crane-sm bg-crane-card-inset p-1.5 shadow-crane-soft",
  {
    variants: {
      size: {
        md: "",
        sm: "p-1",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export const tabTriggerClass = cva(
  cn(
    "inline-flex min-h-10 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-crane-sm font-semibold transition-colors md:min-h-0",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "text-crane-muted-foreground hover:bg-crane-accent hover:text-crane-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring focus-visible:ring-offset-2 focus-visible:ring-offset-crane-background",
    // `data-[state=active]` — TabsTrigger (Radix); `data-[active=true]` — NavLink w NavTabBar.
    // `shadow-crane-soft` po stronie NavLink wyrównuje wygląd do TabsTrigger, który dostaje je z ui/tabs.
    "data-[state=active]:bg-crane-primary data-[state=active]:text-crane-primary-foreground data-[state=active]:hover:bg-crane-primary",
    "data-[active=true]:bg-crane-primary data-[active=true]:text-crane-primary-foreground data-[active=true]:hover:bg-crane-primary data-[active=true]:shadow-crane-soft",
  ),
  {
    variants: {
      size: {
        md: "px-4 py-2 text-xs leading-crane-tab",
        sm: "px-3 py-1.5 text-crane-xs leading-4",
      },
    },
    defaultVariants: { size: "md" },
  },
);

/** Dosuwa aktywną zakładkę do kadru paska wyłącznie w poziomie. `scrollIntoView`
 *  przewinąłby też stronę w pionie, więc pasek przesuwamy sami przez `scrollLeft`. */
const revealActiveTab = (list: HTMLElement | null, selector: string) => {
  const active = list?.querySelector<HTMLElement>(selector);
  if (!list || !active) return;
  const listRect = list.getBoundingClientRect();
  const tabRect = active.getBoundingClientRect();
  if (tabRect.left < listRect.left) list.scrollLeft += tabRect.left - listRect.left;
  else if (tabRect.right > listRect.right) list.scrollLeft += tabRect.right - listRect.right;
};

export interface TabItem {
  value: string;
  label: string;
  /** Licznik przy etykiecie, np. „Aktywne (3)”. */
  count?: number;
  icon?: ReactNode;
  /** Dodatkowe klasy pojedynczej zakładki (np. obwódka zaznaczenia w kreatorze). */
  className?: string;
}

export interface TabBarProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  size?: "md" | "sm";
  /** Treść po prawej stronie paska (akcje, filtry). */
  actions?: ReactNode;
  className?: string;
}

/** Zakładki sterowane stanem — Dashboard, szczegóły urządzenia, ustawienia profilu. */
export const TabBar = ({ items, value, onValueChange, size = "md", actions, className }: TabBarProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    revealActiveTab(listRef.current, '[data-state="active"]');
  }, [value]);

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <Tabs value={value} onValueChange={onValueChange} className="min-w-0 max-w-full">
        <TabsList ref={listRef} className={tabListClass({ size })}>
          {items.map((item) => (
            <TabsTrigger key={item.value} value={item.value} className={cn(tabTriggerClass({ size }), item.className)}>
              {item.icon}
              {item.label}
              {typeof item.count === "number" ? ` (${item.count})` : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
    </div>
  );
};

export interface NavTabItem {
  to: string;
  label: string;
  /** Licznik przy etykiecie, np. „Zgłoszenia klientów (3)” — jak `TabItem.count`. */
  count?: number;
  /** Zakładka aktywna także w podwidokach (/new, /:id/edit…). Domyślnie: dopasowanie ścieżki. */
  match?: (pathname: string) => boolean;
}

export interface NavTabBarProps {
  items: NavTabItem[];
  pathname: string;
  size?: "md" | "sm";
  actions?: ReactNode;
  className?: string;
}

/** Zakładki sterowane trasą — Administracja, RODO, podzakładki modułów. */
export const NavTabBar = ({ items, pathname, size = "md", actions, className }: NavTabBarProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    revealActiveTab(listRef.current, '[data-active="true"]');
  }, [pathname]);

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <div ref={listRef} role="tablist" className={tabListClass({ size })}>
        {items.map((item) => {
          const active = item.match ? item.match(pathname) : pathname.startsWith(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              role="tab"
              aria-selected={active}
              data-active={active}
              className={tabTriggerClass({ size })}
            >
              {item.label}
              {typeof item.count === "number" ? ` (${item.count})` : null}
            </NavLink>
          );
        })}
      </div>
      {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
    </div>
  );
};
